import {
  FIELDS,
  GeneralSection,
  MortgagePropertySection,
  LoanSection,
  BorrowerSection,
} from 'modules/cm/domain/PepperUploadPreview/PepperUploadPreview';
import formatCurrencyValue from 'utils/formatCurrency';
import { formatPostcode } from 'utils/address';
import { format } from 'utils/date';
import { formatSortCode } from 'utils/text';
import { makeBooleanValue } from 'modules/cm/pages/CaseDetailsPage/partials/UnderwritingSummary/viewModel/mappers';

export const makeValue = (value: any): string => {
  return value || '-';
};

const makeCurrency = (value: any): string => {
  return value ? formatCurrencyValue(value) : makeValue(value);
};

const makeDate = (value: any): string => {
  return value ? format(value).fromBackendToView() : makeValue(value);
};

const makeBoolean = (value: any): string => {
  return typeof value === 'boolean' ? makeBooleanValue(value) : makeValue(value);
};

const makeSortCode = (value: any): string => {
  return value ? formatSortCode(value) : makeValue(value);
};
const makePostCode = (value: any): string => {
  return value ? formatPostcode(value) : makeValue(value);
};

export type Labelled = { mapper: (value: any) => string; label: string };

export const GeneralFields: Record<keyof GeneralSection, Labelled> = {
  [FIELDS.CASE_ID]: {
    mapper: (value: any) => makeValue(value),
    label: 'Case ID',
  },
  [FIELDS.ORIGINATION_ID]: {
    mapper: (value: any) => makeValue(value).toUpperCase(),
    label: 'Origination ID',
  },
  [FIELDS.NUMBER_OF_APPLICANTS]: {
    mapper: (value: any) => makeValue(value),
    label: 'Number of applicants',
  },
  [FIELDS.ACCOUNT_NUMBER]: {
    mapper: (value: any) => makeValue(value),
    label: 'Account Number',
  },
  [FIELDS.SORT_CODE]: {
    mapper: (value: any) => makeSortCode(value),
    label: 'Sort Code',
  },
};

export const MortgagePropertyFields: Record<keyof MortgagePropertySection, Labelled> = {
  [FIELDS.TITLE_NUMBER]: {
    mapper: (value: any) => makeValue(value),
    label: 'Title Number',
  },
  [FIELDS.FLAT_NUMBER]: {
    mapper: (value: any) => makeValue(value),
    label: 'Flat Number',
  },
  [FIELDS.BUILDING_NAME]: {
    mapper: (value: any) => makeValue(value),
    label: 'Building Name',
  },
  [FIELDS.BUILDING_NUMBER]: {
    mapper: (value: any) => makeValue(value),
    label: 'Building Number',
  },
  [FIELDS.STREET]: {
    mapper: (value: any) => makeValue(value),
    label: 'Street',
  },
  [FIELDS.CITY]: {
    mapper: (value: any) => makeValue(value),
    label: 'City',
  },
  [FIELDS.PROPERTY_POST_CODE]: {
    mapper: (value: any) => makePostCode(value),
    label: 'Property Postcode',
  },
  [FIELDS.PROPERTY_TYPE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Property Type',
  },
  [FIELDS.TENURE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Tenure',
  },
  [FIELDS.PROPERTY_BUILT_YEAR]: {
    mapper: (value: any) => makeValue(value),
    label: 'Property Built',
  },
  [FIELDS.ACTUAL_PROPERTY_VALUE]: {
    mapper: (value: any) => makeCurrency(value),
    label: 'Actual Property Value',
  },
  [FIELDS.INSPECTION_DATE]: {
    mapper: (value: any) => makeDate(value),
    label: 'Inspection date',
  },
};

export const LoanFields: Record<keyof LoanSection, Labelled> = {
  [FIELDS.LVR]: {
    mapper: (value) => makeValue(value),
    label: 'LiveMore Variable Rate (LVR)',
  },
  [FIELDS.REVERT_MARGIN]: {
    mapper: (value) => makeValue(value),
    label: 'Revert Margin',
  },
  [FIELDS.NUMBER_OF_PAYMENTS]: {
    mapper: (value) => makeValue(value),
    label: 'Number of payments',
  },
  [FIELDS.VARIABLE_TERM_START_DATE]: {
    mapper: (value) => makeDate(value),
    label: 'Variable Term Start Date',
  },
  [FIELDS.PEPPER_PURPOSE_OF_THE_LOAN]: {
    mapper: (value: any) => makeValue(value),
    label: 'Servicing loan purpose',
  },
  [FIELDS.FCA_LOAN_PURPOSE]: {
    mapper: (value: any) => makeValue(value),
    label: 'FCA Loan Purpose',
  },
  [FIELDS.NET_LOAN]: {
    mapper: (value: any) => makeCurrency(value),
    label: 'Net Loan',
  },
  [FIELDS.IS_BUY_TO_LET]: {
    mapper: (value: any) => makeBoolean(value),
    label: 'Buy-to-Let',
  },
  [FIELDS.IS_MARGIN_ACTIVE]: {
    mapper: (value: any) => makeBoolean(value),
    label: 'Margin_Active',
  },
  [FIELDS.IS_APP_ACTIVE]: {
    mapper: (value: any) => makeBoolean(value),
    label: 'App Active',
  },
  [FIELDS.PRODUCT_FEE]: {
    mapper: (value: any) => makeCurrency(value),
    label: 'Product Fee',
  },
  [FIELDS.PRODUCT_FEE_PAYMENT]: {
    mapper: (value: any) => makeBoolean(value),
    label: 'Product Fee Payment',
  },
  [FIELDS.COMPLETION_DATE]: {
    mapper: (value: any) => makeDate(value),
    label: 'Completion date',
  },
  [FIELDS.FIXED_INTEREST_RATE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Fixed Interest Rate',
  },
};

export const SingleBorrowerFields: Record<keyof BorrowerSection[FIELDS.APPLICANT_1], Labelled> = {
  [FIELDS.BORROWER_LAST_NAME]: {
    mapper: (value: any) => makeValue(value),
    label: 'Last Name',
  },
  [FIELDS.BORROWER_FIRST_NAME]: {
    mapper: (value: any) => makeValue(value),
    label: 'First Name',
  },
  [FIELDS.BORROWER_TITLE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Title',
  },
  [FIELDS.BORROWER_DATE_OF_BIRTH]: {
    mapper: (value: any) => makeDate(value),
    label: 'Date of Birth',
  },
  [FIELDS.BORROWER_GENDER]: {
    mapper: (value: any) => makeValue(value),
    label: 'Gender',
  },
  [FIELDS.BORROWER_EMPLOYMENT_STATUS]: {
    mapper: (value: any) => makeValue(value),
    label: 'Employment Status',
  },
  [FIELDS.BORROWER_TOTAL_CURRENT_INCOME]: {
    mapper: (value: any) => makeCurrency(value),
    label: 'Total Current Income',
  },
  [FIELDS.BORROWER_PHONE_HOME]: {
    mapper: (value: any) => makeValue(value),
    label: 'Home number',
  },
  [FIELDS.BORROWER_PHONE_MOBILE]: {
    mapper: (value: any) => makeValue(value),
    label: 'Mobile number',
  },
  [FIELDS.BORROWER_PHONE_WORK]: {
    mapper: (value: any) => makeValue(value),
    label: 'Work number',
  },
  [FIELDS.BORROWER_EMAIL]: {
    mapper: (value: any) => makeValue(value),
    label: 'Email Address',
  },
  [FIELDS.BORROWER_ADDITIONAL_EMAIL]: {
    mapper: (value: any) => makeValue(value),
    label: 'Additional Email',
  },
};

export const BorrowerFields: Record<keyof BorrowerSection, any> = {
  [FIELDS.APPLICANT_1]: SingleBorrowerFields,
  [FIELDS.APPLICANT_2]: SingleBorrowerFields,
};
